/**
 * SignalR class
 */
export default class SignalrService {
  /**
   * Class constructor method
   * @param signalR
   * @param connection
   * @param url
   */
  constructor({ signalR = {}, connection = {}, url = '' }) {
    if (!signalR || !connection) {
      throw new Error('SignalR must exists in current context.');
    }
    /**
     * SignalR client instance
     */
    this.signalR = signalR;
    /**
     * SignalR connection
     * @type {{}}
     */
    this.connection = connection;
    /**
     * Connection state
     * @type {*|{connecting: 0, connected: 1, reconnecting: 2, disconnected: 4}}
     */
    this.connectionState = this.signalR.connectionState;
    /**
     * signalR host
     * @type {string}
     */
    this.url = url;
    /**
     * Transport type
     * @type {string[]}
     */
    this.transportType = ['webSockets', 'longPolling'];
    /**
     * signalR hub
     * @type {*|_babelTraverse.Hub}
     */
    this.hub = this.connection.hub;
    /**
     * Check if signalR is reconnecting
     * @type {boolean}
     */
    this.reconnecting = false;
    /**
     * SignalR Connection states
     * @type {{connecting: number, connected: number, reconnecting: number, disconnected: number}}
     */
    this.STATES = {
      connecting: this.connectionState.connecting, // 0
      connected: this.connectionState.connected, // 1
      reconnecting: this.connectionState.reconnecting, // 2
      disconnected: this.connectionState.disconnected, // 4
    };
    /**
     * Available hubs
     * @type {{}}
     */
    this.hubs = {
      liveHubCompressed: this.connection.liveHubCompressed,
      liveHub: this.connection.liveHub,
      liveMatchesLiveCompressed: this.connection.liveMatchesLiveCompressed,
      liveMatchesLive: this.connection.liveMatchesLive,
      authorizationBetslipsHub: this.connection.authorizationBetslipsHub,
      evaluateBetSlipsHub: this.connection.evaluateBetSlipsHub,
      jackpotHub: this.connection.jackpotHub,
      notification: this.connection.notification,
      recommendation: this.connection.recommendation,
      searchBetSlipsHub: this.connection.searchBetSlipsHub,
      viewBetSlipsHub: this.connection.viewBetSlipsHub,
      cashout: this.connection.cashout,
    };
    /**
     * Hub Events
     * @type {{}}
     */
    this.hubEvents = {
      liveHubCompressed: {
        availableLiveMatches: 'availableLiveMatches',
        oddsChange: 'oddsChange',
      },
      liveHub: {
        availableLiveMatches: 'availableLiveMatches',
        oddsChange: 'oddsChange',
      },
      liveMatchesLiveCompressed: {
        liveChangedOdds: 'liveChangedOdds',
      },
      liveMatchesLive: {
        liveChangedOdds: 'liveChangedOdds',
      },
      authorizationBetslipsHub: {},
      evaluateBetSlipsHub: {},
      jackpotHub: {},
      notification: {
        addNotification: 'addNotification',
        pendingBetSlips: 'pendingBetSlips',
        betSlipPlacement: 'betSlipPlacement',
        negotiateBetslips: 'negotiateBetslips',
        cashOut: 'cashOut',
        printTicket: 'printTicket',
      },
      recommendation: {
        recommendedMatches: 'recommendedMatches',
      },
      searchBetSlipsHub: {},
      viewBetSlipsHub: {},
      cashout: {
        myBets: 'myBets',
      },
    };
    /**
     * connection headers
     * @type {Array}
     */
    this.headers = [];
    /**
     * Subscribe to connection events
     */
    this.setSignalRConnectionEvents();
    /**
     * Should we automatically recconect if signalR is disconnected
     * @type {boolean}
     */
    this.autoRecconect = true;
    /**
     * Success callback
     * @type {null}
     */
    this.successCallback = null;
    /**
     * Error callback
     * @type {null}
     */
    this.errorCallback = null;
  }

  /**
   * Connection to signalR
   * @param successCallback
   * @param errorCallback
   */
  connect(successCallback, errorCallback) {
    const config = {
      transport: this.transportType,
    };
    this.successCallback = successCallback;
    this.errorCallback = errorCallback;
    if (this.url) {
      this.hub.url = this.url;
    }
    this.hub.start(config)
      .then((response) => {
        if (typeof successCallback === 'function') {
          successCallback(response);
        }
      });
  }

  /**
   * Disconnect from signalR
   */
  disconnect() {
    this.connection.hub.stop();
    return Promise.resolve('OK');
  }

  /**
   * Disconnect from liveMatchesLive hub
   */
  disconnectLiveHubClient() {
    if (typeof this.hubs.liveMatchesLive !== 'undefined') {
      this.hubs.liveMatchesLive.server.disconnect(APP_SETTINGS.tenant);
    }
  }

  /**
   * Disconnect from liveMatchesLiveCompressed hub
   */
  disconnectLiveMatchesLiveCompressed() {
    if (typeof this.hubs.liveMatchesLiveCompressed !== 'undefined') {
      this.hubs.liveMatchesLiveCompressed.server.disconnect(APP_SETTINGS.tenant);
    }
  }

  /**
   * Disconnect from live hub
   */
  disconnectLiveHub(onlyShort = false) {
    if (typeof this.hubs.liveHub !== 'undefined') {
      this.hubs.liveHub.server.disconnect(APP_SETTINGS.tenant, APP_SETTINGS.player.language, onlyShort);
    }
  }
  /**
   * Disconnect from live hub
   */
  disconnectLiveCompressedHub(onlyShort = false) {
    if (typeof this.hubs.liveHubCompressed !== 'undefined') {
      this.hubs.liveHubCompressed.server.disconnect(APP_SETTINGS.tenant, APP_SETTINGS.player.language, onlyShort);
    }
  }
  /**
   * Connect to proxy Hub
   * Example generated url will be
   * this.connection.liveMatchesLive.server.connectHome(sportID, languageId, oddFormat, tenantID, externalIDs, isAsian)
   * @param hub
   * @param proxy
   * @param params
   */
  connectToProxyHub(hub = '', proxy = '', ...params) {
    if (typeof this.hubs[hub] === 'undefined') {
      throw new Error(`Hub ${hub} does not exists on client service`);
    }
    return this.hubs[hub].server[proxy].apply(null, params);
  }

  /**
   * Set connection header
   * @param headerName
   * @param headerVal
   */
  setHeader(headerName = '', headerVal = '') {
    this.connection.Headers.Add(headerName, headerVal);
  }

  /**
   * Listen for event on hub client
   * @param hub
   * @param event
   * @param callback
   */
  on(hub, event, callback) {
    if (this.getSignalRConnectionState() === this.STATES.connected) {
      throw new Error(`Event "${event}" must be added before connection has been established to ${hub} hub.`);
    }
    let hubEvent = this.hubEvents[hub][event];
    if (typeof hubEvent === 'undefined') {
      throw new Error(`Undefined event ${event} in service signalRService. Please add the event in hubEvents.`);
    } else {
      this.connection[hub].client[event] = (data, bsgroup) => {
        callback(data, bsgroup);
      };
    }
  }

  /**
   * Check signalR state (connected|disconnected)
   * @returns {*|_babelTraverse.Hub}
   */
  getSignalRConnectionState() {
    return this.hub && this.hub.state;
  }

  /**
   * get the signalR connectionId
   * @param hub
   */
  getConnectionId(hub = '') {
    return this.hubs[hub].connection.id;
  }

  /**
   * Set some events to know when signalR is reconnecting
   */
  setSignalRConnectionEvents() {
    /**
     * Check when hub is reconnected
     */
    this.hub.reconnected(() => {
      this.reconnecting = false;
      // reconnect to the hubs after signalR reconection is done, without starting new connection
      this.successCallback();
    });
    /**
     * Check when hub is reconnecting
     */
    this.hub.reconnecting(() => {
      this.reconnecting = true;
    });
    /**
     * Check when hub is disconnected
     */
    this.hub.disconnected(() => {
      if (this.autoRecconect) {
        setTimeout(() => {
          this.connect(this.successCallback, this.errorCallback);
        }, 1000);
      }
    });
    /**
     * Slow connection event
     */
    this.hub.connectionSlow(() => {
    });
  }

  /**
   * Decompress gzipped data from signalR
   * @param resp
   * @returns {any}
   */
  decompressData(resp) {
    const gzipedStr = atob(resp);
    const ungziped = pako.ungzip(gzipedStr, { to: 'string' });
    return JSON.parse(ungziped.replace(/\0/g, ""));
  }
}
